import Input from '@components/Input';
import {
  mapLocaleCodeToCountry,
  mapLocaleCodeToPlate,
} from '@features/WordPress/utilities/helpers/locale';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import { FormikProps } from 'formik';
import styles from './MultiLicensePlateInput.module.css';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, Fragment } from 'react';
import LicensePlateAddon from './LicensePlateAddon/LicensePlateAddon';
import Button from '@components/Button';
import { Box, FormHelperText, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LandingPageFormValues } from '@features/Campaign/components/DefaultLandingPage/DefaultLandingPage';

const {
  Input: InputClass,
  LpInput,
  Label: LabelClass,
  LicensePlateDisclaimer,
  MultipleLpn,
  BtnMultipleLpn,
} = styles;

interface MultiLicensePlateInputProps {
  inputLabel?: string;
  formik: FormikProps<LandingPageFormValues>;
  whiteInput?: boolean;
  name?: string;
  isCampaign?: boolean;
  additionalTerms?: string;
  allowMultipleCars?: boolean;
  helperText?: string;
}

const MultiLicensePlateInput = ({
  inputLabel,
  formik,
  whiteInput,
  name = 'licensePlate',
  isCampaign = false,
  additionalTerms,
  allowMultipleCars = false,
  helperText,
}: MultiLicensePlateInputProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation(['pip', 'common', 'checkout']);

  const handleLicensePlateChange = (value: string, index: number) => {
    const formattedLicensePlate = value.replace(/\s/g, '').toUpperCase();
    const newPlates = [...(formik.values.licensePlate || [''])];
    newPlates[index] = formattedLicensePlate;

    formik.setFieldTouched(`${name}[${index}]`, true, false);
    formik.setFieldValue(name, newPlates, true);
  };

  const handleRemoveLicensePlate = (index: number) => {
    const newPlates = formik.values.licensePlate.filter((_, i) => i !== index);

    // Clear corresponding error
    let newErrors: string[] | undefined = Array.isArray(
      formik.errors.licensePlate
    )
      ? formik.errors.licensePlate.filter((_, i) => i !== index)
      : undefined;

    // Only clean up undefined values if there's exactly one plate remaining
    if (newPlates.length === 1 && Array.isArray(newErrors)) {
      newErrors = newErrors.filter((error): error is string => !!error);
    }

    formik.setFieldValue(name, newPlates.length ? newPlates : ['']);
    formik.setFieldError(name, newErrors?.length ? newErrors : undefined);
  };

  const handleAddMoreLicensePlates = () => {
    const newPlates = [...(formik.values.licensePlate || ['']), ''];
    formik.setFieldValue(name, newPlates);
  };

  const licensePlates = Array.isArray(formik.values.licensePlate)
    ? formik.values.licensePlate
    : [''];

  const onePlateAdded = licensePlates.length === 1;

  return (
    <div className={LpInput}>
      {inputLabel && (
        <label className={LabelClass} htmlFor={inputLabel}>
          {inputLabel}
        </label>
      )}

      {isCampaign && (
        <p className={LicensePlateDisclaimer}>{additionalTerms}</p>
      )}

      {licensePlates.map((plate, index) => {
        const individualError = formik.errors.licensePlate
          ? formik.errors.licensePlate[index]
          : undefined;

        const errorMessage = onePlateAdded
          ? formik.errors.licensePlate
          : individualError;

        return (
          <Fragment key={`license-plate-${index}`}>
            <Box width="100%" display="flex" alignItems="flex-start">
              <Box width="100%">
                <Input
                  name={`${name}[${index}]`}
                  readableName={
                    plate.toUpperCase() || t('plate', { ns: 'common' })
                  }
                  value={plate}
                  addon={<LicensePlateAddon error={Boolean(errorMessage)} />}
                  style={{
                    display: 'block',
                    ...(whiteInput && { backgroundColor: '#fff' }),
                  }}
                  type="text"
                  placeholder={mapLocaleCodeToPlate(locale).placeholder}
                  className={classnames(InputClass, MultipleLpn)}
                  showValidIcon={!errorMessage}
                  onBlur={formik.handleBlur}
                  error={
                    errorMessage
                      ? { message: errorMessage as string }
                      : undefined
                  }
                  errorMessage={
                    (errorMessage as string) || t('error', { ns: 'common' })
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleLicensePlateChange(event.target.value, index);
                  }}
                />
              </Box>

              {allowMultipleCars && licensePlates.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveLicensePlate(index)}
                  sx={{ marginLeft: 0.5, marginTop: 1.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Fragment>
        );
      })}

      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      {allowMultipleCars && (
        <Button
          size="small"
          fullWidth
          type="button"
          hasArrowLeft={false}
          hasArrow={false}
          onClick={handleAddMoreLicensePlates}
          className={BtnMultipleLpn}
        >
          {t('addExtraCar', { ns: 'checkout' })}
        </Button>
      )}
    </div>
  );
};

export default MultiLicensePlateInput;
